import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button, Card, useToast } from '@nako/ui';
import { useAuthStore } from '@/stores/auth';
import { useReceiptsStore } from '@/stores/receipts';
import { useUiStore } from '@/stores/ui';
import { makeT } from '@/lib/i18n';
import { resizeImage } from '@/lib/image';

export default function ReceiptScan() {
  const business = useAuthStore((s) => s.business)!;
  const enqueue = useReceiptsStore((s) => s.enqueue);
  const flushQueue = useReceiptsStore((s) => s.flushQueue);
  const lang = useUiStore((s) => s.lang);
  const t = makeT(lang);
  const toast = useToast();
  const navigate = useNavigate();

  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFile = async (rawFile: File) => {
    setBusy(true);
    try {
      // Downscale before upload — modern phones produce 8-12 MB JPEGs that
      // exceed Supabase Edge Function payload limits and burn customer data.
      const file = await resizeImage(rawFile, { maxBytes: 1_800_000, maxEdge: 1920 });
      setPreview(URL.createObjectURL(file));
      await enqueue(business.id, file);
      // Try immediate flush. If offline, the receipt sits in queue.
      if (navigator.onLine) {
        await flushQueue();
        toast.show('Receipt queued — OCR running.', { tone: 'success' });
      } else {
        toast.show('Saved offline. Will sync when online.', { tone: 'info' });
      }
      navigate('/app/receipts');
    } catch (e) {
      toast.show('Could not save receipt.', { tone: 'error' });
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const onCamera = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const onGallery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  return (
    <main className="max-w-mobile mx-auto px-4 py-6">
      <h1 className="font-display mb-4 text-2xl">Scan receipt</h1>

      <Card className="text-center">
        {preview ? (
          <img
            src={preview}
            alt="Captured receipt"
            className="rounded-card mb-4 max-h-96 w-full object-contain"
          />
        ) : (
          <div className="bg-paper-2 rounded-card text-muted mb-4 flex aspect-[3/4] items-center justify-center">
            <Camera size={48} aria-hidden="true" />
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            onClick={() => cameraRef.current?.click()}
            loading={busy}
            leftIcon={<Camera size={18} />}
          >
            {t('receipts.take_photo')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => galleryRef.current?.click()}
            disabled={busy}
            leftIcon={<ImageIcon size={18} />}
          >
            {t('receipts.upload')}
          </Button>
        </div>

        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onCamera}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onGallery}
        />

        {busy && (
          <p className="text-muted mt-4 flex items-center justify-center gap-2 text-sm">
            <Loader2 className="animate-spin" size={16} /> Uploading…
          </p>
        )}
      </Card>

      <p className="text-muted mt-4 text-center text-xs">
        Image is hashed locally. Duplicates of the same receipt are skipped automatically.
      </p>
    </main>
  );
}
